import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { AlertCircle, ArrowRight, CheckCircle2, ChevronLeft, Clock, Info, Lock, RefreshCw, Store, XCircle } from 'lucide-react';
import ContactField from '@/components/auth/ContactField';
import OtpAuthLayout from '@/components/auth/OtpAuthLayout';
import {
  Alert,
  Button,
  Combobox,
  FileDropzone,
  Icon,
  InlineStatus,
  KeyValueList,
  OtpInput,
  ProductThumb,
  ResultState,
  StatusPill,
  StepProgress,
  StepTransition,
  TextField,
  WhatsAppIcon,
} from '@/components/common';
import { PROTECTION_BRANDS } from '@/data/protectionBrands';
import { WHATSAPP_SUPPORT_URL, whatsappUrl } from '@/lib/support';
import type { LoginContact } from '@/service/otpAuthService';
import { LinkedPlan, planRecoveryAvailable, RecoveryResult, sendRecoveryCode, submitPlanRecovery } from '@/service/planRecoveryService';
import { loginSuccess } from '@/store/slices/authSlice';
import { formatFileSize } from '@/utils/files';
import { ContactChannel, contactError, displayContact, toContact } from '@/utils/loginContact';
import { formatDay } from '@/utils/productCases';
import { formatEventTime } from '@/utils/shipment';

type Step = 'order' | 'contact' | 'code' | 'linked' | 'review' | 'no_match' | 'limited';

const OTP_LENGTH = 6;
const MAX_INVOICE_BYTES = 5 * 1024 * 1024;
const INVOICE_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];
const CONTACT_LABELS = { mobile: 'Mobile number you’ll use from now on', email: 'Email you’ll use from now on' };
/** Progress bars: part-way through "order details", then part-way through "verify". */
const PROGRESS: Partial<Record<Step, number>> = { order: 0.6, contact: 1.6, code: 1.6 };

const formatCountdown = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
const today = () => new Date().toISOString().slice(0, 10);

interface OrderErrors {
  brand?: string;
  orderId?: string;
  orderDate?: string;
  invoice?: string;
}

const Heading: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="tw-flex tw-flex-col tw-gap-2">
    <h1 className="tw-text-[28px] tw-font-bold tw-leading-[34px] tw-tracking-[-0.28px] tw-text-ds-text-primary">{title}</h1>
    <p className="tw-text-[15px] tw-leading-[22px] tw-text-ds-text-secondary">{children}</p>
  </div>
);

const Footnote: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="tw-flex tw-items-start tw-gap-2 tw-text-[12px] tw-leading-4 tw-text-ds-text-secondary">
    <Icon icon={Info} size={14} className="tw-mt-px tw-text-ds-text-tertiary" />
    {children}
  </p>
);

const backLinkClass =
  'tw-flex tw-items-center tw-gap-1 tw-self-start tw-rounded tw-text-[14px] tw-font-semibold tw-leading-5 tw-text-ds-brand-text hover:tw-underline focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-ds-brand';

/**
 * F1–F5A "Find your plan": order details + invoice → verify a mobile or email → linked, under
 * review, or no match. Recovery only — an invoice never creates cover, and no plan details are
 * shown until the match is confirmed.
 */
const FindPlanPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [step, setStep] = useState<Step>('order');

  // F1 — order details
  const [brandId, setBrandId] = useState<string>();
  const [orderId, setOrderId] = useState('');
  const [orderDate, setOrderDate] = useState('');
  const [invoice, setInvoice] = useState<File | null>(null);
  const [invoiceError, setInvoiceError] = useState<string>();
  const [showErrors, setShowErrors] = useState(false);

  // F2/F3 — contact and code
  const [channel, setChannel] = useState<ContactChannel>('mobile');
  const [mobile, setMobile] = useState('');
  const [email, setEmail] = useState('');
  const [touched, setTouched] = useState(false);
  const [hasAccount, setHasAccount] = useState(false);
  const [sentTo, setSentTo] = useState<LoginContact | null>(null);
  const [otp, setOtp] = useState('');
  const [codeError, setCodeError] = useState<string>();
  const [resendIn, setResendIn] = useState(0);
  const [loading, setLoading] = useState(false);

  // Results
  const [outcome, setOutcome] = useState<RecoveryResult | null>(null);
  const [signIn, setSignIn] = useState<{ token: string; plan?: LinkedPlan; user: Extract<RecoveryResult, { status: 'linked' }>['customer']['user'] } | null>(null);

  const available = planRecoveryAvailable();
  const brand = PROTECTION_BRANDS.find((b) => b.id === brandId);
  const contact = toContact(channel, mobile, email);
  const fieldError = contactError(channel, mobile, email, touched);
  const isMobile = channel === 'mobile';
  const word = isMobile ? 'number' : 'email';
  const cleanOrderId = orderId.trim();
  const orderLabel = cleanOrderId.startsWith('#') || !/^\d+$/.test(cleanOrderId) ? cleanOrderId : `#${cleanOrderId}`;

  useEffect(() => {
    if (resendIn <= 0) return;
    const id = window.setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => window.clearTimeout(id);
  }, [resendIn]);

  const go = (next: Step) => {
    setStep(next);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const orderErrors: OrderErrors = {
    brand: brand ? undefined : 'Choose the brand you bought from',
    orderId: !cleanOrderId
      ? 'Enter the order ID'
      : brand?.orderId && !brand.orderId.pattern.test(cleanOrderId)
        ? `${brand.name} order IDs look like ${brand.orderId.example}`
        : undefined,
    orderDate: !orderDate ? 'Enter the order date' : orderDate > today() ? 'Order date can’t be in the future' : undefined,
    invoice: invoiceError ?? (invoice ? undefined : 'Upload the invoice'),
  };
  const orderValid = !Object.values(orderErrors).some(Boolean);
  // Show missing fields only after a first Continue; a bad file shows straight away (F1C).
  const visibleErrors: OrderErrors = showErrors ? orderErrors : { invoice: invoiceError };

  const pickInvoice = (file: File) => {
    setInvoice(file);
    if (!INVOICE_TYPES.includes(file.type)) setInvoiceError('Upload the invoice as a PDF, JPG or PNG.');
    else if (file.size > MAX_INVOICE_BYTES) setInvoiceError(`This file is ${formatFileSize(file.size)}. Upload a PDF, JPG or PNG under 5 MB.`);
    else setInvoiceError(undefined);
  };

  const sendCode = async (to: LoginContact, existingAccount = false) => {
    setLoading(true);
    try {
      const res = await sendRecoveryCode(to, existingAccount);
      switch (res.status) {
        case 'sent':
          setSentTo(to);
          setOtp('');
          setCodeError(undefined);
          setResendIn(res.resendInSeconds);
          go('code');
          break;
        case 'has_account':
          setHasAccount(true);
          break;
        case 'cooldown':
          setResendIn(res.seconds);
          toast.error(res.message);
          break;
        case 'not_linked':
          break;
        default:
          toast.error(res.message);
      }
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const submit = async () => {
    if (!brand || !invoice || !sentTo) return;
    setLoading(true);
    try {
      const res = await submitPlanRecovery({ brand, orderId: cleanOrderId, orderDate, invoice, contact: sentTo, otp });
      if (res.status === 'invalid_code') setCodeError(res.message);
      else if (res.status === 'failed') toast.error(res.message);
      else {
        if (res.status === 'linked') setSignIn({ token: res.token, plan: res.plan, user: res.customer.user });
        setOutcome(res);
        go(res.status);
      }
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // The verified contact becomes the customer's login; start the session only when they continue.
  const goToDashboard = () => {
    if (signIn?.token) {
      const { user } = signIn;
      dispatch(
        loginSuccess({
          user: { name: user.name, email: user.email, phone: user.phone ?? null, address: (user.address as string) ?? null },
          accessToken: signIn.token,
          loginType: 'regular',
        }),
      );
    }
    navigate('/customer/policies', { replace: true });
  };

  const chatButton = (
    <Button
      variant="secondary"
      leadingIcon={WhatsAppIcon}
      fullWidth
      href={brand && cleanOrderId ? whatsappUrl(`Hi, I need help finding my plan. Brand: ${brand.name}, order ${orderLabel}.`) : WHATSAPP_SUPPORT_URL}
      target="_blank"
    >
      Chat on WhatsApp
    </Button>
  );

  const back = (to: Step | 'login', label: string) =>
    to === 'login' ? (
      <Link to="/customer-login" className={backLinkClass}>
        <Icon icon={ChevronLeft} size={16} />
        {label}
      </Link>
    ) : (
      <button type="button" onClick={() => go(to)} className={backLinkClass}>
        <Icon icon={ChevronLeft} size={16} />
        {label}
      </button>
    );

  const progress = PROGRESS[step] !== undefined && <StepProgress current={PROGRESS[step]!} total={2} />;
  const contactText = sentTo ? displayContact(sentTo) : '';

  const render = () => {
    switch (step) {
      case 'order':
        return (
          <form
            className="tw-flex tw-flex-col tw-gap-5"
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              setShowErrors(true);
              if (available && orderValid) go('contact');
            }}
          >
            {back('login', 'Back to sign in')}
            <Heading title="Find your plan">Share your order details and we’ll match them to your plan.</Heading>
            {progress}
            {!available && (
              <>
                <Alert title="Finding your plan online is coming soon">
                  For now, chat with us on WhatsApp with your order ID and invoice, and we’ll link your plan for you.
                </Alert>
                {chatButton}
              </>
            )}
            <Combobox
              label="Brand you bought from"
              placeholder="Search brand, e.g. EUME"
              options={PROTECTION_BRANDS.map((b) => ({ value: b.id, label: b.name }))}
              value={brandId}
              onChange={setBrandId}
              error={visibleErrors.brand}
              renderEmpty={(query) => (
                <div className="tw-flex tw-flex-col tw-items-start tw-gap-3">
                  <div className="tw-flex tw-flex-col tw-gap-1">
                    <p className="tw-text-[15px] tw-font-semibold tw-leading-5 tw-text-ds-text-primary">No brands match “{query}”</p>
                    <p className="tw-text-[13px] tw-leading-[18px] tw-text-ds-text-secondary">
                      We can only find plans bought from brands that offer Protega protection. Check your order confirmation for a Protega plan, or chat with
                      us.
                    </p>
                  </div>
                  <Button variant="secondary" size="sm" leadingIcon={WhatsAppIcon} href={WHATSAPP_SUPPORT_URL} target="_blank">
                    Chat on WhatsApp
                  </Button>
                </div>
              )}
            />
            <div className="tw-grid tw-grid-cols-2 tw-gap-3">
              <TextField
                label="Order ID"
                placeholder={brand?.orderId?.example ?? '#100245'}
                value={orderId}
                onChange={(e) => setOrderId(e.target.value.slice(0, 40))}
                autoComplete="off"
                error={visibleErrors.orderId}
              />
              <TextField
                label="Order date"
                type="date"
                max={today()}
                value={orderDate}
                onChange={(e) => setOrderDate(e.target.value)}
                error={visibleErrors.orderDate}
              />
            </div>
            <FileDropzone
              label="Upload invoice"
              file={invoice}
              onFile={pickInvoice}
              onRemove={() => {
                setInvoice(null);
                setInvoiceError(undefined);
              }}
              accept="application/pdf,image/jpeg,image/png"
              hint="PDF, JPG or PNG, up to 5 MB"
              error={visibleErrors.invoice}
            />
            <Button type="submit" fullWidth disabled={!available || (showErrors && !orderValid)}>
              Continue
            </Button>
            <Footnote>
              {showErrors && !orderValid
                ? 'Fix the highlighted fields to continue.'
                : 'Next, verify a mobile number or email by OTP. Matching orders are linked instantly; others are reviewed within 24–48 hours.'}
            </Footnote>
          </form>
        );

      case 'contact':
        return (
          <form
            className="tw-flex tw-flex-col tw-gap-5"
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              if (contact && !hasAccount) sendCode(contact);
            }}
          >
            {back('order', 'Back to order details')}
            <Heading title="Verify your contact">Choose the mobile number or email you’ll use to sign in from now on.</Heading>
            {progress}
            {!hasAccount && brand && (
              <div className="tw-flex tw-items-center tw-gap-3 tw-rounded-2xl tw-bg-ds-subtle tw-p-4">
                <span className="tw-flex tw-h-10 tw-w-10 tw-shrink-0 tw-items-center tw-justify-center tw-text-ds-brand-text">
                  <Icon icon={Store} size={20} />
                </span>
                <span className="tw-flex tw-min-w-0 tw-flex-col tw-gap-0.5">
                  <span className="tw-text-[15px] tw-font-semibold tw-leading-5 tw-text-ds-text-primary">
                    {brand.name} · Order {orderLabel}
                  </span>
                  <span className="tw-text-[13px] tw-leading-[18px] tw-text-ds-text-secondary">Ordered {formatDay(orderDate)} · invoice uploaded</span>
                </span>
              </div>
            )}
            <ContactField
              channel={channel}
              onChannelChange={(c) => {
                setChannel(c);
                setTouched(false);
                setHasAccount(false);
              }}
              mobile={mobile}
              onMobileChange={(v) => {
                setMobile(v);
                setHasAccount(false);
              }}
              email={email}
              onEmailChange={(v) => {
                setEmail(v);
                setHasAccount(false);
              }}
              labels={CONTACT_LABELS}
              onBlur={() => setTouched(true)}
              error={fieldError}
            />
            <StepTransition stepKey={hasAccount ? 'has-account' : 'send'} className="tw-flex tw-flex-col tw-gap-5">
              {hasAccount && contact ? (
                <>
                  <Alert tone="warning" title={`This ${word} already has a Protega account`}>
                    Sign in with {displayContact(contact)} and we’ll add this plan to that account, or use a different {word}.
                  </Alert>
                  <Button fullWidth loading={loading} onClick={() => sendCode(contact, true)}>
                    Sign in with this {word}
                  </Button>
                  <Button
                    variant="secondary"
                    fullWidth
                    onClick={() => {
                      setHasAccount(false);
                      if (isMobile) setMobile('');
                      else setEmail('');
                      setTouched(false);
                    }}
                  >
                    Use a different {word}
                  </Button>
                </>
              ) : (
                <>
                  <Button type="submit" fullWidth disabled={!contact} loading={loading}>
                    {isMobile ? 'Send code on WhatsApp' : 'Send code by email'}
                  </Button>
                  <Footnote>We’ll link the plan to this {word} so you can sign in with it next time.</Footnote>
                </>
              )}
            </StepTransition>
          </form>
        );

      case 'code':
        return (
          <form
            className="tw-flex tw-flex-col tw-gap-5"
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              if (otp.length === OTP_LENGTH) submit();
            }}
          >
            {back('contact', 'Back to contact details')}
            <Heading title="Enter the code">
              Sent {sentTo?.channel === 'email' ? 'by email' : 'on WhatsApp'} to {contactText}.
            </Heading>
            {progress}
            <div className="tw-flex tw-flex-col tw-gap-2">
              <p className="tw-text-[13px] tw-font-medium tw-leading-[18px] tw-text-ds-text-secondary">Enter the 6-digit code</p>
              <OtpInput
                value={otp}
                onChange={(v) => {
                  setOtp(v);
                  setCodeError(undefined);
                }}
                length={OTP_LENGTH}
                error={Boolean(codeError)}
                disabled={loading}
                autoFocus
                label="6-digit code"
              />
              {codeError && (
                <InlineStatus icon={AlertCircle} tone="danger">
                  {codeError}
                </InlineStatus>
              )}
              <p className="tw-text-[12px] tw-leading-4 tw-text-ds-text-tertiary">
                Didn’t get it?{' '}
                {resendIn > 0 ? (
                  `Resend in ${formatCountdown(resendIn)}`
                ) : (
                  <button
                    type="button"
                    disabled={loading}
                    onClick={() => sentTo && sendCode(sentTo, hasAccount)}
                    className="tw-font-semibold tw-text-ds-brand-text hover:tw-underline disabled:tw-opacity-60"
                  >
                    Resend code
                  </button>
                )}
              </p>
            </div>
            <Button type="submit" fullWidth disabled={otp.length < OTP_LENGTH} loading={loading}>
              Verify and link plan
            </Button>
          </form>
        );

      case 'linked':
        return (
          <div className="tw-flex tw-flex-col tw-gap-5">
            <ResultState
              tone="success"
              icon={CheckCircle2}
              title="Plan linked"
              description={`Your ${brand?.name} plan is now linked to ${contactText}. Use this ${sentTo?.channel === 'email' ? 'email' : 'number'} to sign in from now on.`}
            />
            {signIn?.plan && (
              <div className="tw-flex tw-items-center tw-gap-[14px] tw-rounded-2xl tw-border tw-border-ds-border-default tw-bg-ds-surface tw-p-4">
                <ProductThumb src={signIn.plan.imageUrl} alt={signIn.plan.productTitle} />
                <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-[3px]">
                  <p className="tw-text-[15px] tw-font-semibold tw-leading-5 tw-text-ds-text-primary">{signIn.plan.productTitle}</p>
                  <p className="tw-text-[13px] tw-leading-[18px] tw-text-ds-text-secondary">
                    {[brand?.name, signIn.plan.orderLabel ?? orderLabel, signIn.plan.planName].filter(Boolean).join(' · ')}
                  </p>
                </div>
                <StatusPill tone="success">Protected</StatusPill>
              </div>
            )}
            <Button fullWidth trailingIcon={ArrowRight} onClick={goToDashboard}>
              Go to my dashboard
            </Button>
          </div>
        );

      case 'review': {
        const review = outcome?.status === 'review' ? outcome : undefined;
        return (
          <div className="tw-flex tw-flex-col tw-gap-5">
            <ResultState
              tone="info"
              icon={Clock}
              title="We’re checking your order"
              description={`We couldn’t match it instantly, so our team will review it within 24–48 hours. We’ll update you ${
                sentTo?.channel === 'email' ? 'by email at' : 'on WhatsApp at'
              } ${contactText}.`}
            />
            <div className="tw-rounded-2xl tw-border tw-border-ds-border-default tw-bg-ds-surface tw-px-4 tw-py-1">
              <KeyValueList
                flush
                rows={[
                  review?.requestId && { label: 'Request ID', value: review.requestId },
                  brand && { label: 'Brand', value: brand.name },
                  { label: 'Order ID', value: orderLabel },
                  review?.submittedAt && { label: 'Submitted', value: formatEventTime(review.submittedAt, { withYear: true }) },
                ].filter((row): row is { label: string; value: string } => Boolean(row))}
              />
            </div>
            <Button fullWidth onClick={() => navigate('/customer-login')}>
              Back to sign in
            </Button>
            {chatButton}
          </div>
        );
      }

      case 'no_match': {
        const noMatch = outcome?.status === 'no_match' ? outcome : undefined;
        return (
          <div className="tw-flex tw-flex-col tw-gap-5">
            <ResultState
              tone="danger"
              icon={XCircle}
              title="We couldn’t match these details"
              description={`${brand?.name} has no order ${orderLabel} dated ${formatDay(orderDate)} with a Protega plan. Check the order ID and date on your invoice and try again.`}
            />
            <Button fullWidth leadingIcon={RefreshCw} onClick={() => go('order')}>
              Try again
            </Button>
            {chatButton}
            {noMatch?.requestId && (
              <Footnote>
                Request {noMatch.requestId}
                {noMatch.reviewedAt && ` · reviewed ${formatDay(noMatch.reviewedAt)}`}
              </Footnote>
            )}
          </div>
        );
      }

      case 'limited':
        return (
          <div className="tw-flex tw-flex-col tw-gap-5">
            <ResultState
              tone="danger"
              icon={Lock}
              title="Too many attempts for this order"
              description={outcome?.status === 'limited' ? outcome.message : undefined}
            />
            {chatButton}
            <Button variant="secondary" fullWidth onClick={() => navigate('/customer-login')}>
              Back to sign in
            </Button>
          </div>
        );
    }
  };

  return (
    <OtpAuthLayout panel="find-plan">
      <StepTransition stepKey={step}>{render()}</StepTransition>
    </OtpAuthLayout>
  );
};

export default FindPlanPage;
